// ============================================================
// sessionTransfer - what goes into a session file, and what comes out.
//
// The pure half of useImportExport: the hook owns the dialogs, the file
// picker and the sinks; this decides the file's contents on the way out
// and reads, normalizes and summarizes them on the way in. No React, no
// DOM - a session file in, plain objects out.
// ============================================================
import { handoutsToFile, handoutsFromFile } from '../engine/handouts';
import { toMapFile, readMapFile } from '../engine/battlefield/mapFile';
import { normalizeTab, normalizeAtkProfiles } from '../engine/importValidation';
import { isSafeDocId, remapUnsafeIds } from '../engine/importIds';
import {
  rosterToFile,
  rosterFromFile,
  loadoutsFromFile,
  isRosterFile,
  SESSION_FILE_VERSION,
} from '../engine/roster/sessionFile';

/** `{ [key]: make() }` when `wanted`, else nothing: one optional section of the file. */
const section = (wanted, key, make) => (wanted ? { [key]: make() } : {});

/** Target tabs as exported: with their narrative only when it was asked for. */
const tabsForFile = (tabs, withNarrative) => (withNarrative ? tabs : tabs.map((t) => ({ ...t, narrativeLog: [] })));

/**
 * v4: the roster IS the session.
 *
 * `combatState: false` writes a clean template - full wounds, no
 * conditions, full magazines - which is what you hand another GM. Under
 * the unified model "attack profiles" and "target tabs" are the same
 * people, so the roster rides along if EITHER is wanted - unchecking one
 * must never silently export half a combatant.
 */
function rosterSection(sel, src) {
  if (!sel.attackProfiles && !sel.targetTabs) return {};
  const roster = rosterToFile(src.roster, {
    includeState: sel.combatState !== false,
    includeNarrative: !!sel.narrativeLogs,
  });
  return { roster, activeTab: src.activeTab };
}

/**
 * The loadouts, then the v3 compatibility payload: still emitted so a file
 * exported here opens in a pre-v7 build, and read back by THIS build only
 * when `roster` is absent.
 */
function legacySections(sel, src) {
  return {
    ...section(sel.loadouts && src.loadouts.length, 'loadouts', () => src.loadouts),
    ...section(sel.attackProfiles, 'atkProfiles', () => src.atkProfiles),
    ...section(sel.targetTabs, 'tabs', () => tabsForFile(src.tabs, sel.narrativeLogs)),
    ...section(sel.targetTabs, 'activeTab', () => src.activeTab),
    ...section(sel.targetTemplates, 'tgtTemplates', () => src.tgtTemplates),
  };
}

/**
 * The battlefield, the staged rooms and the handouts.
 *
 * Terrain and the library go through the SAME serialiser as a map file,
 * so a map inside a session file and a map inside a map file are
 * byte-identical structures and one reader does for both. Positions are
 * their own checkbox: handing another GM your map is usual, handing them
 * your fight already in progress is not. The waiting rooms are a
 * SCENARIO, STAGED - off by default in the dialog, because the file may go
 * to a player and an ambush they can read is not one.
 */
function fieldSections(sel, src) {
  const { terrain } = src;
  return {
    ...section(sel.battlefield && terrain, 'battlefield', () =>
      toMapFile([{ name: terrain.name || 'Battlefield', terrain }]),
    ),
    ...section(sel.mapLibrary && src.mapLibrary.length, 'mapLibrary', () => toMapFile(src.mapLibrary)),
    ...section(sel.tokenPositions && Object.keys(src.tokens).length, 'tokens', () => src.tokens),
    ...section(sel.waitingRooms && src.waitingRooms.length, 'waitingRooms', () => src.waitingRooms),
    ...section(sel.handouts && src.handouts.length, 'handouts', () => handoutsToFile(src.handouts)),
  };
}

/**
 * The session file for the export dialog's selections.
 *
 * @param {Object} sel - the dialog's checkboxes (attackProfiles, targetTabs, ...)
 * @param {Object} src - roster, activeTab, loadouts, atkProfiles, tabs,
 *   tgtTemplates, terrain, mapLibrary, tokens, waitingRooms, handouts
 * @param {string} exportedAt - ISO timestamp stamped into the file
 * @returns {Object} the file, keys in the order they have always been written
 */
export function sessionExport(sel, src, exportedAt) {
  return {
    version: SESSION_FILE_VERSION,
    exportedAt,
    ...rosterSection(sel, src),
    ...legacySections(sel, src),
    ...fieldSections(sel, src),
  };
}

// ────────────────────────────────────────────────────────────
// IMPORT
// ────────────────────────────────────────────────────────────

const NOT_A_SESSION = 'Import failed: file does not contain a valid session object.';
const EMPTY_TABS = 'Import failed: tabs must be a non-empty array.';

/**
 * The v4 roster, if the file has one. A v4 file also carries the v3
 * tabs/atkProfiles payload for older builds; when the roster is present it
 * WINS (see readLegacy).
 */
function readRoster(data, warnings) {
  if (!isRosterFile(data)) return { importedRoster: null, importedLoadouts: null };
  const { combatants, skipped } = rosterFromFile(data.roster);
  const importedLoadouts = loadoutsFromFile(data.loadouts);
  if (skipped > 0) warnings.push({ field: `roster (${skipped} unreadable entr${skipped === 1 ? 'y' : 'ies'})` });
  return { importedRoster: combatants, importedLoadouts };
}

/** Normalize each tab, collecting what had to be fixed. */
function normalizeTabs(rawTabs, warnings) {
  return rawTabs.map((rawTab) => {
    const { tab, warnings: fixed } = normalizeTab(rawTab);
    warnings.push(...fixed);
    return tab;
  });
}

/**
 * The v3 payload - only when there is no roster: it is the richer, unified
 * record, and reading both would import every combatant twice. An error
 * when the tabs are not a non-empty array.
 */
function readLegacy(data, importedRoster, warnings) {
  if (importedRoster) return { normalizedProfiles: null, normalizedTabs: null };
  let normalizedProfiles = null;
  if (data.atkProfiles) {
    const { atkProfiles, warnings: fixed } = normalizeAtkProfiles(data.atkProfiles);
    warnings.push(...fixed);
    normalizedProfiles = atkProfiles;
  }
  if (!data.tabs) return { normalizedProfiles, normalizedTabs: null };
  if (!Array.isArray(data.tabs) || data.tabs.length === 0) return { error: EMPTY_TABS };
  return { normalizedProfiles, normalizedTabs: normalizeTabs(data.tabs, warnings) };
}

/**
 * A map file inside the session: its maps, or null. One that will not
 * parse is a warning, never a refusal: the roster in the same file is
 * worth importing on its own, and a broken map must not cost somebody
 * their people.
 */
function readMaps(file, label, warnings) {
  if (!file) return null;
  const { maps, error } = readMapFile(file);
  if (!error) return maps;
  warnings.push({ field: `${label} (${error})` });
  return null;
}

const objectOrNull = (value) => (value && typeof value === 'object' ? value : null);

/**
 * The tokens, each written as `battlefield/{key}`: one whose key cannot
 * name a document is left out, and every token's id IS its key (a token is
 * its combatant's position, keyed by the combatant).
 */
function readTokens(tokens) {
  if (!objectOrNull(tokens)) return null;
  const usable = Object.entries(tokens).filter(([key, token]) => isSafeDocId(key) && objectOrNull(token));
  return Object.fromEntries(usable.map(([key, token]) => [key, { ...token, id: key }]));
}

/** Everything besides the people: templates, the battlefield, rooms, handouts. */
function readRest(data, warnings) {
  return {
    normalizedTemplates: objectOrNull(data.tgtTemplates),
    importedTerrain: readMaps(data.battlefield, 'battlefield', warnings)?.[0]?.terrain ?? null,
    importedMapLibrary: readMaps(data.mapLibrary, 'map library', warnings),
    importedTokens: readTokens(data.tokens),
    // Only ever present if whoever exported it ticked the box.
    importedRooms: Array.isArray(data.waitingRooms) ? data.waitingRooms : null,
    importedHandouts: handoutsFromFile(data.handouts),
  };
}

const hasNarrativeIn = (list) => !!list?.some((x) => x.narrativeLog?.length > 0);

const isInjured = (c) => c.state.conditions?.length > 0 || c.state.woundsCurrent < (c.sheet.wounds?.max ?? Infinity);

/** What the import dialog shows and offers for a parsed file. */
function importDialogFor(raw, version, warnings) {
  return {
    atkProfiles: raw.normalizedProfiles,
    tabs: raw.normalizedTabs,
    tgtTemplates: raw.normalizedTemplates,
    roster: raw.importedRoster,
    loadouts: raw.importedLoadouts,
    hasNarrative: hasNarrativeIn(raw.normalizedTabs) || hasNarrativeIn(raw.importedRoster),
    hasCombatState: !!raw.importedRoster?.some(isInjured),
    warnings,
    version: version || 1,
    hasBattlefield: !!raw.importedTerrain,
    mapCount: raw.importedMapLibrary?.length ?? 0,
    tokenCount: raw.importedTokens ? Object.keys(raw.importedTokens).length : 0,
    roomCount: raw.importedRooms ? raw.importedRooms.length : 0,
    handoutCount: raw.importedHandouts.length,
  };
}

/** `pick` of each entry of a list the file may or may not hold. */
const idsOf = (list, pick) => (Array.isArray(list) ? list.map((x) => pick(x ?? {})) : []);

/** The v3 halves' ids: a tab's id is reused as its combatant's, and a bound character keeps its own. */
function legacyIds(data) {
  const bindings = Object.values(objectOrNull(data.atkProfiles?._charBindings) ?? {});
  return [
    ...idsOf(data.tabs, (t) => t.id),
    ...idsOf(data.tabs, (t) => t.sourceCharacter?.id),
    ...bindings.map((b) => b?.character?.id),
  ];
}

/**
 * Every id in the file that becomes a document id once it is applied:
 * combatants/{id}, characters/{sheet id} when a sheet is saved to the
 * library, battlefield/{token}, loadouts/{id}. See engine/importIds.
 */
function sessionFileIds(data) {
  return [
    ...idsOf(data.roster, (c) => c.id),
    ...idsOf(data.roster, (c) => c.sheet?.id),
    ...idsOf(data.loadouts, (l) => l.id),
    ...Object.keys(objectOrNull(data.tokens) ?? {}),
    ...legacyIds(data),
  ];
}

/**
 * Parse an already-decoded file into the confirm step's data and the
 * dialog's. Ids are checked FIRST, on the whole file, so every reference to
 * a replaced one (the active tab, a token's key, a loadout link) follows it.
 */
function readSession(file) {
  if (typeof file !== 'object' || file === null || Array.isArray(file)) return { error: NOT_A_SESSION };
  const data = remapUnsafeIds(file, sessionFileIds(file));
  const warnings = [];
  const roster = readRoster(data, warnings);
  const legacy = readLegacy(data, roster.importedRoster, warnings);
  if (legacy.error) return legacy;
  const raw = { ...legacy, ...roster, ...readRest(data, warnings), activeTab: data.activeTab };
  return { raw, dialog: importDialogFor(raw, data.version, warnings) };
}

/**
 * Read a session file's text.
 *
 * @returns {{ raw: Object, dialog: Object } | { error: string }} `raw` is
 *   what the confirm step applies, `dialog` what the import dialog shows;
 *   `error` is the sentence for the log when the file cannot be imported.
 */
export function readSessionText(text) {
  let data;
  try {
    data = JSON.parse(text);
  } catch (parseErr) {
    return { error: 'Import failed: invalid JSON - ' + parseErr.message };
  }
  try {
    return readSession(data);
  } catch (err) {
    return { error: 'Import failed: ' + err.message };
  }
}

// ────────────────────────────────────────────────────────────
// APPLY
// ────────────────────────────────────────────────────────────

/** The roster supersedes both v3 halves - see readLegacy. */
function applyRoster(raw, sel, sinks) {
  if (!raw.importedRoster || sel.roster === false || !sinks.importRoster) return;
  sinks.importRoster(raw.importedRoster, {
    keepState: sel.combatState !== false,
    keepNarrative: !!sel.narrativeLogs,
    activeTab: raw.activeTab,
  });
  if (raw.importedLoadouts?.length && sinks.importLoadouts) sinks.importLoadouts(raw.importedLoadouts);
}

function applyTabs(raw, sel, sinks) {
  const tabs = tabsForFile(raw.normalizedTabs, sel.narrativeLogs);
  sinks.setTabs(tabs);
  sinks.setActiveTab(raw.activeTab || tabs[0].id);
  sinks.syncAllTabsToFirestore(tabs);
}

/**
 * The v3 halves. A v3 session file still describes attackers as named
 * profiles with _charBindings; importAtkProfiles runs them through the
 * roster migration, so a bound character arrives as a combatant rather
 * than an untyped binding on a shared document.
 */
function applyLegacy(raw, sel, sinks) {
  if (sel.attackProfiles && raw.normalizedProfiles) sinks.importAtkProfiles(raw.normalizedProfiles);
  if (sel.targetTabs && raw.normalizedTabs) applyTabs(raw, sel, sinks);
  if (sel.targetTemplates && raw.normalizedTemplates) {
    sinks.setTgtTemplates((prev) => ({ ...prev, ...raw.normalizedTemplates }));
  }
}

const hasField = (raw) => !!(raw.importedTerrain || raw.importedMapLibrary || raw.importedTokens);

/** The battlefield sections, each null unless it was ticked. */
function fieldImport(raw, sel) {
  return {
    terrain: sel.battlefield ? raw.importedTerrain : null,
    library: sel.mapLibrary ? raw.importedMapLibrary : null,
    tokens: sel.tokenPositions ? raw.importedTokens : null,
    waitingRooms: sel.waitingRooms ? raw.importedRooms : null,
  };
}

function applyField(raw, sel, sinks) {
  if (sinks.importBattlefield && hasField(raw)) sinks.importBattlefield(fieldImport(raw, sel));
  if (sel.handouts && raw.importedHandouts?.length && sinks.importHandouts) sinks.importHandouts(raw.importedHandouts);
}

/**
 * Hand the parsed file's selected sections to their sinks, in the order
 * they have always been applied: roster, v3 halves, battlefield, handouts.
 *
 * @param {Object} raw - `readSessionText(...).raw`
 * @param {Object} sel - the import dialog's checkboxes
 * @param {Object} sinks - importRoster, importLoadouts, importAtkProfiles,
 *   setTabs, setActiveTab, syncAllTabsToFirestore, setTgtTemplates,
 *   importBattlefield, importHandouts (the optional ones may be absent)
 */
export function applySessionImport(raw, sel, sinks) {
  applyRoster(raw, sel, sinks);
  applyLegacy(raw, sel, sinks);
  applyField(raw, sel, sinks);
}

/**
 * The log line for what normalization had to fix, and the tab it belongs
 * on - or null when nothing was fixed.
 */
export function importWarningEntry(raw, warnings, activeTab) {
  if (warnings.length === 0) return null;
  const tabId = raw.normalizedTabs ? raw.activeTab || raw.normalizedTabs[0]?.id || activeTab : activeTab;
  const text = `Import: normalized ${warnings.length} field(s) - ${warnings.map((w) => w.field).join(', ')}`;
  return { tabId, text };
}
